import { useEffect } from 'react';

import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { WhiteLabelType } from 'application-types';

import { createThemedKlaroConfig } from '../config/klaro';
import { initializeMixpanel } from '../helpers/analytics';
import { initializeDatadog } from '../ddSpanMonitor';

type UseKlaroConsentOptions = {
  whitelabel?: WhiteLabelType | null;
};

let klaroInitialized = false;
let klaroModule: Awaited<
  typeof import('klaro/dist/klaro-no-translations')
> | null = null;

/**
 * Show the Klaro consent banner to allow users to manage their cookie preferences.
 * This can be called from anywhere in the app (e.g., a "Cookie Preferences" link).
 * Klaro must have been initialized first via useKlaroConsent hook.
 */
export function showCookieConsentBanner(): void {
  if (!klaroModule || !klaroInitialized) {
    console.warn('[Consent] Klaro is not initialized yet. Cannot show banner.');

    return;
  }

  klaroModule.show();
}

/**
 * Manages Klaro cookie consent banner initialization.
 * When disabled or Klaro fails to load, falls back to direct analytics initialization.
 */
export function useKlaroConsent({ whitelabel }: UseKlaroConsentOptions): void {
  const klaroEnabled = useFlag(FeatureFlag.KlaroCookieConsent, false);

  useEffect(() => {
    // Skip consent initialization if feature flag is not enabled
    if (!klaroEnabled) {
      initializeMixpanel();
      initializeDatadog();

      return;
    }

    // Skip if whitelabel hasn't loaded yet
    if (!whitelabel?.profile) {
      return;
    }

    if (klaroInitialized) {
      return;
    }

    (async () => {
      try {
        // Import Klaro without bundled translations (so our translations work for all languages)
        const klaro = await import('klaro/dist/klaro-no-translations');

        // Create themed config using whitelabel colors
        const themedConfig = createThemedKlaroConfig({
          primaryColor: whitelabel.profile?.backgroundColor,
          secondaryColor: whitelabel.profile?.theme?.palette?.secondary,
          name: whitelabel.profile?.name,
          privacyPolicyUrl:
            whitelabel.privacyPolicyUrls?.[0] || whitelabel.privacyPolicy,
        });

        // Check storage BEFORE setup() to determine if user needs to consent
        const storageName = themedConfig.storageName || 'klaro';
        const hasConsentCookie = document.cookie
          .split('; ')
          .some(c => c.startsWith(`${storageName}=`));
        const hasConsentLocal = localStorage.getItem(storageName) !== null;
        const needsConsent = !hasConsentCookie && !hasConsentLocal;

        // Initialize Klaro with themed config
        klaro.setup(themedConfig);

        klaroModule = klaro; // Cache for showCookieConsentBanner()
        klaroInitialized = true;

        // Show banner only if user hasn't made a consent decision yet
        if (needsConsent) {
          klaro.show(themedConfig);
        }
      } catch (err) {
        // Safe-fail: if Klaro fails to load, initialize analytics directly
        console.error('[Consent] Failed to initialize Klaro:', err);
        // Reset module reference to maintain consistency with klaroInitialized
        klaroModule = null;
      }
    })();
  }, [klaroEnabled, whitelabel]);
}

/**
 * Reset the Klaro initialization state. Used for testing only.
 * @internal
 */
export function resetKlaroState(): void {
  klaroInitialized = false;
  klaroModule = null;
}
